/**
 * /download — get the Oddits Sync desktop agent.
 * @file src/web/features/main/pages/download.tsx
 *
 * A public page, deliberately. The person installing this is usually the
 * CLIENT on their own PC — the machine Tally actually runs on — not the CA who
 * has an oddits login. Putting it behind auth would mean the CA had to fetch
 * the installer and pass it on, which is exactly the friction the agent exists
 * to remove. So it renders under the marketing layout, no login.
 *
 * The installer URL comes from VITE_SYNC_DOWNLOAD_URL rather than being
 * hardcoded, because where the binary lives is a hosting decision that has not
 * been made yet (the app repo is private, so GitHub Releases are not publicly
 * downloadable). With the variable unset the page says the build is not
 * published yet — an honest empty state beats a button that 404s.
 */

import { Link } from 'react-router-dom';
import { Download, Laptop, ShieldCheck, RefreshCw, CheckCircle2 } from 'lucide-react';
import { SEO } from '../../../shared/components';

const DOWNLOAD_URL =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_SYNC_DOWNLOAD_URL ?? '';
const VERSION =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_SYNC_VERSION ?? '';

const STEPS: [string, string][] = [
  ['Install it on the PC that runs Tally', 'It reads Tally locally over its own gateway — the books never leave that machine except to your oddits account.'],
  ['Open the company in TallyPrime', 'The agent lists whatever companies are open and lets you pick one.'],
  ['Sign in with your oddits login', 'Use the email and password the oddits team gave you. That ties this PC to your account — nothing syncs until you sign in.'],
  ['Press Sync', 'Masters and vouchers are pushed up. Changes made in oddits wait for your confirmation before they are written back into Tally.'],
];

export default function DownloadPage() {
  return (
    <>
      <SEO title="Download Oddits Sync" />

      <div className="mx-auto max-w-3xl px-6 py-14">
        <span className="grid size-14 place-items-center rounded-2xl bg-inkblack text-highlighter">
          <Laptop className="h-7 w-7" />
        </span>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight text-ink-900">Oddits Sync</h1>
        <p className="mt-2 max-w-xl text-ink-600">
          A small desktop app for the computer that runs TallyPrime. It sends that
          company's books to oddits, so nobody has to export XML by hand.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          {DOWNLOAD_URL ? (
            <a
              href={DOWNLOAD_URL}
              className="inline-flex items-center gap-2 rounded-full bg-inkblack px-6 py-3 text-sm font-semibold text-paper no-underline transition hover:bg-inkblack-80"
            >
              <Download className="h-4 w-4" /> Download for Windows
            </a>
          ) : (
            <div className="rounded-xl border border-warn-200 bg-warn-50 px-4 py-3 text-sm text-warn-700">
              The installer is not published yet. Ask your accountant for the
              current build — this page will carry it once it is released.
            </div>
          )}
          <span className="text-xs text-ink-500">
            Windows 10 or later, 64-bit{VERSION ? ` · version ${VERSION}` : ''}
          </span>
        </div>

        <div className="mt-10 rounded-2xl border border-ink-200 bg-paper p-6">
          <h2 className="text-base font-semibold text-ink-900">Setting it up</h2>
          <ol className="mt-4 space-y-4">
            {STEPS.map(([title, detail], i) => (
              <li key={title} className="flex gap-3">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-ink-100 text-xs font-semibold text-ink-700">{i + 1}</span>
                <span>
                  <span className="block text-sm font-medium text-ink-900">{title}</span>
                  <span className="mt-0.5 block text-sm text-ink-600">{detail}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-ink-200 bg-paper p-5">
            <ShieldCheck className="h-5 w-5 text-ink-400" />
            <p className="mt-3 text-sm font-semibold text-ink-900">It only reads, until you say otherwise</p>
            <p className="mt-1 text-sm text-ink-600">
              Syncing pulls from Tally. Anything your accountant changes in oddits is
              queued and shown to you first — nothing is written into your books
              without you confirming it.
            </p>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-paper p-5">
            <RefreshCw className="h-5 w-5 text-ink-400" />
            <p className="mt-3 text-sm font-semibold text-ink-900">Run it whenever you like</p>
            <p className="mt-1 text-sm text-ink-600">
              Each sync sends what has changed since the last one. Leave Tally open on
              the company you want to send, and press Sync.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-ink-200 bg-ink-50/60 p-5">
          <p className="text-sm font-semibold text-ink-900">What it needs</p>
          <ul className="mt-2 space-y-1.5">
            {['TallyPrime running, with the company open', 'Tally’s gateway enabled (it is, by default, on port 9000)', 'Your oddits login — the email and password from the oddits team'].map((r) => (
              <li key={r} className="flex items-start gap-2 text-sm text-ink-600">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" /> {r}
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-8 text-sm text-ink-500">
          Trouble pairing? <Link to="/contact" className="font-medium text-inkblack">Get in touch</Link>.
        </p>
      </div>
    </>
  );
}
